/**
 * **Le bouton « Exporter » de la barre supérieure, et ce qu'il fait enfin.**
 *
 * `AppShell` le rendait `<button type="button">`, sans le moindre gestionnaire.
 * Il ne faisait rien, il n'avait jamais rien fait, et il était sur les **seize
 * pages** — les onze écrans provisoires compris. Trois commandes avaient été
 * refusées dans un panneau faute de route servie pendant qu'un bouton inerte
 * tenait la barre de tous les écrans. C'est la même faute, en pire : plus elle
 * est visible, plus elle apprend à ne pas cliquer.
 *
 * ## Ni le câbler partout, ni le retirer partout
 *
 * Le câbler dans la coquille était impossible **honnêtement** : la coquille ne
 * sait pas ce qu'un écran tient. Sur les onze écrans provisoires, elle ne tient
 * rien du tout — un export y aurait écrit un fichier vide sous un nom qui
 * promet des données.
 *
 * Le retirer partout aurait effacé du dessin un élément que deux écrans peuvent
 * réellement servir.
 *
 * **Il est donc rendu là où il marche, et nulle part ailleurs.** Un écran qui
 * tient des lignes déclare son export ; la barre montre le bouton et l'appelle.
 * Un écran qui ne tient rien ne déclare rien, et la barre ne montre pas de
 * bouton. C'est la même conduite que partout dans ce produit : ce qui n'existe
 * pas ne se dessine pas.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** Ce que la barre supérieure sait de l'export du moment. */
interface ExportRegistry {
  /** La commande à appeler, ou `null` quand l'écran n'a rien à écrire. */
  readonly run: (() => void) | null;
  /** Déclare — ou retire — la commande de l'écran courant. */
  readonly declare: (run: (() => void) | null) => void;
}

const Contexte = createContext<ExportRegistry | null>(null);

/** Porte la déclaration d'export au travers de la coquille. */
export function ExportProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [run, setRun] = useState<(() => void) | null>(null);
  // `setRun(() => …)` et non `setRun(…)` : React traiterait une fonction passée
  // nue comme une mise à jour fonctionnelle et l'APPELLERAIT au lieu de la
  // retenir — l'export partirait au montage de l'écran.
  const declare = useCallback((commande: (() => void) | null) => {
    setRun(() => commande);
  }, []);
  const valeur = useMemo(() => ({ run, declare }), [declare, run]);
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

/** Ce que la barre supérieure lit. Hors coquille, aucun export n'est déclaré. */
export function useTopbarExport(): (() => void) | null {
  return useContext(Contexte)?.run ?? null;
}

/**
 * Déclare l'export de l'écran courant, et le **retire à son démontage**.
 *
 * Sans le retrait, le bouton survivrait au changement d'écran et exporterait
 * les lignes de l'écran précédent sous le titre du suivant — la pire issue
 * possible pour une commande qui écrit un fichier qu'on relira ailleurs.
 *
 * Passer `null` retire la déclaration sans démonter : c'est le cas d'un écran
 * qui n'a rien reçu, ou dont la file est vide.
 */
export function useDeclareExport(run: (() => void) | null): void {
  const registre = useContext(Contexte);
  const declare = registre?.declare;
  useEffect(() => {
    if (declare === undefined) return undefined;
    declare(run);
    return () => {
      declare(null);
    };
  }, [declare, run]);
}
